import React from 'react';
import Box from '@mui/material/Box';
import { Switch, dsV2 } from '@bloomays-lib/ui.shared';

/**
 * Primitives de formulaire du Design System V2 (TEC-3051).
 *
 * Les primitives génériques (`FieldV2`, `InputV2`, `TextareaV2`, `SelectV2`, `Row2`, `InfoV2`,
 * `DividerV2`, `CheckRow`) ont été promues dans `ui.shared` (atom `FormFieldsV2`, coexistence DS V2
 * — motif `ButtonV2`/`ModalV2`) ; elles sont ré-exportées ici pour préserver les imports existants.
 * Restent co-localisées avec les popins/drawer du tableau de bord : `ToggleSection`,
 * `SectionHeading`, `EstCard`.
 */

export { FieldV2, InputV2, TextareaV2, SelectV2, Row2, InfoV2, DividerV2, CheckRow } from '@bloomays-lib/ui.shared';
export type { FieldTone, InfoTone, SelectV2Option } from '@bloomays-lib/ui.shared';

/** Section activable par interrupteur (`.av-toggle` + `.av-ttl` + `.av-desc`). */
export const ToggleSection = ({
  title,
  description,
  checked,
  onChange,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children?: React.ReactNode;
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', margin: '4px 0' }}>
      <Switch checked={checked} onSwitch={(_e, value) => onChange(value)} />
      <Box sx={{ fontSize: '14.5px', fontWeight: 700, color: dsV2.color.noir }}>{title}</Box>
    </Box>
    {checked ? (
      <>
        {description ? (
          <Box sx={{ fontSize: 12, color: dsV2.color.t3, margin: '0 0 12px', lineHeight: 1.5 }}>{description}</Box>
        ) : null}
        {children}
      </>
    ) : null}
  </Box>
);

/** Intertitre de section (`.av-subhead`). */
export const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ fontSize: 14, fontWeight: 700, color: dsV2.color.noir, margin: '18px 0 10px' }}>{children}</Box>
);

/** Carte d'estimation chiffrée (`.est-card` + `.est-lbl` + `.est-val`). */
export const EstCard = ({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'bleu' | 'vert' }) => (
  <Box
    sx={{
      background: dsV2.color.surface2,
      border: `1px solid ${dsV2.color.borderLight}`,
      borderRadius: dsV2.radius.sm,
      padding: '14px 16px',
    }}
  >
    <Box
      sx={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: dsV2.color.t4,
        marginBottom: '6px',
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: 22,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: tone === 'bleu' ? dsV2.color.bleu : tone === 'vert' ? dsV2.color.vert : dsV2.color.noir,
      }}
    >
      {value}
    </Box>
  </Box>
);
